import { useCallback, useEffect, useRef, useState } from 'react';
import { getDepositoCobrancas } from '@spark/services/deposito';
import { notification } from '@spark/ui-components/utils/notification';
import {
  cancelarCobrancaDepositoPorTipo,
  getApiErrorMessage,
  mapCobrancaListItem,
  mapTipoCobranca
} from '../../Cobrancas/utils/cobrancasDepositoUtils';

export const useDepositoNotaFiscalCobrancas = (numeroNF, show) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [cancelandoCobranca, setCancelandoCobranca] = useState(null);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 50
  });

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    const numeroNFFiltro = String(numeroNF || '').trim();
    if (!show || !numeroNFFiltro) {
      setData([]);
      setTotalRegistros(0);
      setLoading(false);
      return;
    }

    if (!isMountedRef.current) return;

    try {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);

      const response = await getDepositoCobrancas({
        NumeroNF: numeroNFFiltro,
        PaginaAtual: paginationState.pageIndex + 1,
        QuantidadePorPagina: paginationState.pageSize
      });

      if (!isMountedRef.current || requestId !== requestIdRef.current) return;

      const responseData = response?.data || response || {};
      const registros = Array.isArray(responseData.registros) ? responseData.registros : [];

      setData(registros.map(mapCobrancaListItem));
      setTotalRegistros(responseData.totalRegistros || 0);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Erro ao buscar cobranças da nota fiscal:', error);
      setData([]);
      setTotalRegistros(0);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  }, [numeroNF, paginationState.pageIndex, paginationState.pageSize, show]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancelarCobranca = useCallback(
    async (row) => {
      const protocolo = row?.protocoloPedidoTMS;
      const tipoCobranca = mapTipoCobranca(row?.tipoCobranca);

      if (!tipoCobranca || !protocolo) return;
      if (!window.confirm('Confirma o cancelamento desta cobranca?')) return;

      try {
        setCancelandoCobranca({ tipoCobranca, protocolo });
        await cancelarCobrancaDepositoPorTipo(tipoCobranca, protocolo);
        notification.success('Cobranca cancelada com sucesso!');
        await fetchData();
      } catch (error) {
        notification.error(getApiErrorMessage(error, 'Erro ao cancelar cobranca'));
        console.error('Erro ao cancelar cobranca:', error);
      } finally {
        setCancelandoCobranca(null);
      }
    },
    [fetchData]
  );

  return {
    data,
    loading,
    totalRegistros,
    paginationState,
    setPaginationState,
    cancelandoCobranca,
    handleCancelarCobranca,
    refreshData: fetchData
  };
};
